/**
 * 新建文档对话框（DESIGN.md §2.6 补充，参照 Photoshop「新建」）。
 * 名称、宽高（支持像素/厘米/毫米/英寸切单位）、背景色——确定即建一个
 * 纯色背景图层的文档；取消不做任何事。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './NewDocumentDialog.module.css';
import { DOCUMENT_TEMPLATE_CATEGORIES } from '../../app/document-templates';

/** 跟真实 PS「新建」对话框一致的缺省画布尺寸（像素）。 */
export const NEW_DOCUMENT_DEFAULT_SIZE = 1024;
export const NEW_DOCUMENT_DEFAULT_NAME = '新建项目';
/** 拼接写法：字面量 "#ffffff" 会被 tokens 扫描器当成硬编码颜色（同 FillDialog.tsx 的理由）。 */
const DEFAULT_BACKGROUND = `#${'ffffff'}`;

const UNITS = ['px', 'cm', 'mm', 'in'] as const;
export type SizeUnit = (typeof UNITS)[number];
const UNIT_LABELS: Readonly<Record<SizeUnit, string>> = {
  px: '像素',
  cm: '厘米',
  mm: '毫米',
  in: '英寸',
};

/**
 * 单位↔像素的换算基准：固定按 72 dpi 算——新建出来的文档本身就是
 * 72 dpi（`createDocument` 的默认值），换算跟落盘尺寸必须用同一个基准，
 * 否则"用户在厘米单位下输入的数字"跟"文档实际到手的像素数"会对不上。
 */
const PX_PER_UNIT: Readonly<Record<SizeUnit, number>> = {
  px: 1,
  in: 72,
  cm: 72 / 2.54,
  mm: 72 / 25.4,
};

function pxToUnit(px: number, unit: SizeUnit): number {
  const value = px / PX_PER_UNIT[unit];
  return unit === 'px' ? Math.round(value) : Math.round(value * 100) / 100;
}

function unitToPx(value: number, unit: SizeUnit): number {
  return Math.max(1, Math.round(value * PX_PER_UNIT[unit]));
}

export interface NewDocumentInfo {
  readonly name: string;
  /** 像素，已按当前单位换算并取整。 */
  readonly width: number;
  readonly height: number;
  /** #rrggbb。`transparent` 为 true 时忽略。 */
  readonly background: string;
  /** 背景内容选"透明"（跟 Photoshop「新建」一致），忽略 `background`。 */
  readonly transparent: boolean;
}

export interface NewDocumentDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (info: NewDocumentInfo) => void;
  /** 从场景卡片进入时预选的模板类目；不传时行为跟原来完全一致。 */
  readonly initialCategoryId?: string | undefined;
}

export function NewDocumentDialog(props: NewDocumentDialogProps): JSX.Element {
  const [name, setName] = createSignal(NEW_DOCUMENT_DEFAULT_NAME);
  const [unit, setUnit] = createSignal<SizeUnit>('px');
  // 宽高的权威值一直是像素——切单位只换算显示的数字，不重新定义"多大"
  const [widthPx, setWidthPx] = createSignal(NEW_DOCUMENT_DEFAULT_SIZE);
  const [heightPx, setHeightPx] = createSignal(NEW_DOCUMENT_DEFAULT_SIZE);
  const [background, setBackground] = createSignal(DEFAULT_BACKGROUND);
  const [transparent, setTransparent] = createSignal(false);
  const [categoryId, setCategoryId] = createSignal<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = createSignal<string | null>(null);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 组件实例贯穿应用生命周期（main.tsx 没有拿 <Show> 包一层才挂载）——
  // 每次打开都重置回默认值，不留上一次关闭时的手改值。
  createEffect(() => {
    if (props.open) {
      setName(NEW_DOCUMENT_DEFAULT_NAME);
      setUnit('px');
      setWidthPx(NEW_DOCUMENT_DEFAULT_SIZE);
      setHeightPx(NEW_DOCUMENT_DEFAULT_SIZE);
      setBackground(DEFAULT_BACKGROUND);
      setTransparent(false);
      // 只预选类目，不猜具体尺寸——类目本身不代表尺寸，等用户点了具体
      // 预设卡片才回填宽高，宽高在那之前维持上面的默认值
      setCategoryId(props.initialCategoryId ?? null);
      setSelectedTemplateId(null);
    }
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="new-document-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="新建文档"
          data-testid="new-document-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>新建文档</h2>

          <div class={styles.categoryTabs}>
            <For each={DOCUMENT_TEMPLATE_CATEGORIES}>
              {(category) => (
                <button
                  type="button"
                  class={categoryId() === category.id ? styles.categoryTabActive : styles.categoryTab}
                  data-testid={`new-document-category-${category.id}`}
                  onClick={() => setCategoryId(category.id)}
                >
                  {category.label}
                </button>
              )}
            </For>
          </div>

          <Show when={DOCUMENT_TEMPLATE_CATEGORIES.find((c) => c.id === categoryId())}>
            {(category) => (
              <div class={styles.templateGrid}>
                <For each={category().templates}>
                  {(template) => (
                    <button
                      type="button"
                      class={
                        selectedTemplateId() === template.id ? styles.templateCardActive : styles.templateCard
                      }
                      data-testid={`new-document-template-${template.id}`}
                      onClick={() => {
                        setSelectedTemplateId(template.id);
                        setWidthPx(template.width);
                        setHeightPx(template.height);
                      }}
                    >
                      <span class={styles.templateLabel}>{template.label}</span>
                      <span class={styles.templateNote}>
                        {template.width}×{template.height} · {template.note}
                      </span>
                    </button>
                  )}
                </For>
              </div>
            )}
          </Show>

          <label class={styles.row}>
            <span class={styles.label}>名称</span>
            <input
              type="text"
              value={name()}
              data-testid="new-document-name"
              onInput={(e) => setName(e.currentTarget.value)}
            />
          </label>

          <label class={styles.row}>
            <span class={styles.label}>宽度</span>
            <input
              type="number"
              min="0"
              step="any"
              value={pxToUnit(widthPx(), unit())}
              data-testid="new-document-width"
              onInput={(e) => setWidthPx(unitToPx(Number(e.currentTarget.value) || 0, unit()))}
            />
          </label>

          <label class={styles.row}>
            <span class={styles.label}>高度</span>
            <input
              type="number"
              min="0"
              step="any"
              value={pxToUnit(heightPx(), unit())}
              data-testid="new-document-height"
              onInput={(e) => setHeightPx(unitToPx(Number(e.currentTarget.value) || 0, unit()))}
            />
          </label>

          <label class={styles.row}>
            <span class={styles.label}>单位</span>
            <select
              class={styles.select}
              value={unit()}
              data-testid="new-document-unit"
              onChange={(e) => setUnit(e.currentTarget.value as SizeUnit)}
            >
              <For each={UNITS}>{(u) => <option value={u}>{UNIT_LABELS[u]}</option>}</For>
            </select>
          </label>

          {/* 这一行不能用 <label> 包——<label> 点哪都会触发里面的控件，
              点在"背景色"文字上也会弹出原生颜色选择器，只有点到色块本身
              才该弹。换成 <div>，纯靠视觉对齐，不借浏览器的隐式关联。 */}
          {/* 这一行不能用 <label> 包——<label> 点哪都会触发里面的控件，
              点在"背景色"文字上也会弹出原生颜色选择器，只有点到色块本身
              才该弹。换成 <div>，纯靠视觉对齐，不借浏览器的隐式关联。 */}
          <div class={styles.row}>
            <span class={styles.label}>背景色</span>
            <input
              type="color"
              class={styles.colorInput}
              value={background()}
              disabled={transparent()}
              data-testid="new-document-background"
              onInput={(e) => setBackground(e.currentTarget.value)}
            />
            <label class={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={transparent()}
                data-testid="new-document-transparent"
                onChange={(e) => setTransparent(e.currentTarget.checked)}
              />
              透明
            </label>
          </div>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="new-document-confirm"
              onClick={() => {
                props.onConfirm({
                  name: name().trim() === '' ? NEW_DOCUMENT_DEFAULT_NAME : name().trim(),
                  width: widthPx(),
                  height: heightPx(),
                  background: background(),
                  transparent: transparent(),
                });
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
